import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { metricsApi } from '../../services/metricsApi';
import { scheduleDeferredWork } from '../../utils/deferredWork';

type TrendMode = 'incidents' | 'open_closed' | 'confirmed_dismissed';
type FlowPoint = { time: string; created: number; closed: number };

const formatTickLabel = (value: string) => {
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return value;
  return dateValue.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const IncidentTrendCard: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<TrendMode>('incidents');
  const [flowData, setFlowData] = useState<FlowPoint[]>([]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const analytics = await metricsApi.getAnalyticsData(24 * 7);
        if (!active) return;
        setFlowData(Array.isArray(analytics.incident_flow_over_time) ? analytics.incident_flow_over_time : []);
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || 'Unable to load incident trend.');
      } finally {
        if (active) setLoading(false);
      }
    };
    scheduleDeferredWork(() => {
      if (active) void run();
    }, { timeoutMs: 400 });
    return () => {
      active = false;
    };
  }, []);

  const chartData = useMemo(() => {
    return (flowData || []).map((point) => ({
      label: formatTickLabel(point.time),
      incidents: Number(point.created || 0),
      open: Math.max(0, Number(point.created || 0) - Number(point.closed || 0)),
      closed: Number(point.closed || 0),
      confirmed: 0,
      dismissed: 0,
    }));
  }, [flowData]);

  const body = (
    <>
      <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: embedded ? 1 : 1.5 }}>
        {embedded ? null : (
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Pattern Trend</Typography>
          <Typography variant="body2" color="text.secondary">
            Last 7 days
          </Typography>
        </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            clickable
            color={mode === 'incidents' ? 'primary' : 'default'}
            label="Incidents"
            onClick={() => setMode('incidents')}
          />
          <Chip
            size="small"
            clickable
            color={mode === 'open_closed' ? 'primary' : 'default'}
            label="Active vs Resolved"
            onClick={() => setMode('open_closed')}
          />
          <Tooltip title="Confirmation outcomes will appear here in a later release.">
            <span>
              <Chip size="small" label="Confirmed vs dismissed" disabled />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rectangular" height={188} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={188}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
              <RechartsTooltip formatter={(v: any, name: string) => [v, name]} labelFormatter={(l) => l} />
              {mode === 'incidents' ? (
                <Line type="monotone" dataKey="incidents" stroke="#1d4f91" strokeWidth={3} dot={false} name="Reported incidents" />
              ) : (
                <>
                  <Line type="monotone" dataKey="open" stroke="#b45309" strokeWidth={3} dot={false} name="Still active" />
                  <Line type="monotone" dataKey="closed" stroke="#2f855a" strokeWidth={2.5} dot={false} name="Resolved" />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
          {mode === 'confirmed_dismissed' && (
            <Typography variant="caption" color="text.secondary">
              Confirmation outcome telemetry is not available yet.
            </Typography>
          )}
          {!chartData.length && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No incident trend data is available yet.
            </Typography>
          )}
        </>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ height: '100%' }}>{body}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, height: '100%' }}>
      {body}
    </Paper>
  );
};

export default IncidentTrendCard;
